"use client"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { Search, Heart, ShoppingCart, User, X, Menu, Moon, Sun, ArrowRight } from "lucide-react"
import Link from "next/link"
import { useCart } from "@/lib/cart-context"
import { useWishlist } from "@/lib/wishlist-context"
import { useTheme } from "@/lib/theme-context"
import { useAuthStore } from "@/lib/auth-store"
import { Input } from "@/components/ui/input"
import { useEffect, useState } from "react"
import Image from "next/image"

export function Header() {
  const { totalItems } = useCart()
  const { totalItems: wishlistItems } = useWishlist()
  const { theme, toggleTheme } = useTheme()
  const { isAuthenticated, user } = useAuthStore()

  // Replace the search button with this dynamic search component
  const [showSearch, setShowSearch] = useState(false)
  const [showTopBanner, setShowTopBanner] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")

  const handleBannerShow = () => {
    const expireTime = Date.now() + 86400000 // 24 hours in ms
    localStorage.setItem('luxury-jeff-topBannerClosedUntil', expireTime.toString())
    setShowTopBanner(false)
  }

  useEffect(() => {
    const ejectInit = async () => {
      const closedUntil = localStorage.getItem("luxury-jeff-topBannerClosedUntil")
      if (closedUntil) {
        const now = Date.now()
        if (now < parseInt(closedUntil)) {
          setShowTopBanner(false)
        }
      }
    }

    ejectInit().then()
  }, [])

  return (
    <>
      {/* Top Banner */}
      {
        showTopBanner &&
        <div className="bg-amber-900 text-white px-4 py-2 text-sm flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="hidden sm:block">Support: support@luxuryjeff.com</span>
          </div>
          <div className="flex items-center gap-2 text-center">
            <span className="text-xs sm:text-sm">Sign up and GET 25% OFF for your first order.</span>
            <Link href={isAuthenticated ? "/profile" : "/auth/signup"} className="underline font-medium">
              {isAuthenticated ? "My Account" : "Sign up now"}
            </Link>
          </div>
          <Button variant="ghost" onClick={() => handleBannerShow()} size="sm" className="text-white hover:text-gray-900/80">
            <X className="h-4 w-4" />
          </Button>
        </div>
      }

      {/* Header */}
      <header className="border-b bg-white dark:bg-gray-900 dark:border-gray-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            {/* <div className="w-8 h-8 bg-amber-900 rounded-full flex items-center justify-center text-white font-bold">
              L
            </div> */}
            {/* <span className="text-xl font-bold text-gray-900 dark:text-white hidden sm:block">Luxury Jeff</span> */}
            <Image src="/logo.png" alt="" width={150}
              height={50}
              className="rounded-lg" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            <Link href="/" className="text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400">
              Home
            </Link>
            <Link
              href="/shop"
              className="text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400"
            >
              Shop
            </Link>
            <Link
              href="/shop?category=women"
              className="text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400"
            >
              Women
            </Link>
            <Link
              href="/shop?category=men"
              className="text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400"
            >
              Men
            </Link>
            <Link
              href="/shop?category=kids"
              className="text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400"
            >
              Kids
            </Link>
            <Link
              href="/about"
              className="text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400"
            >
              About Us
            </Link>
            <Link
              href="/contact"
              className="text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400"
            >
              Contact Us
            </Link>
          </nav>

          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={toggleTheme} className="text-gray-700 dark:text-gray-300">
              {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>

            {showSearch ? (
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-10 h-9"
                    autoFocus
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => setSearchQuery("")}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-700 dark:text-gray-300"
                  onClick={() => {
                    // Handle search submission
                    if (searchQuery.trim()) {
                      window.location.href = `/shop?search=${encodeURIComponent(searchQuery.trim())}`
                    }
                  }}
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-700 dark:text-gray-300"
                  onClick={() => {
                    setShowSearch(false)
                    setSearchQuery("")
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-700 dark:text-gray-300"
                  onClick={() => setShowSearch(true)}
                >
                  <Search className="h-5 w-5" />
                </Button>

                {!showSearch && (
                  <>
                    <Link href="/wishlist">
                      <Button variant="ghost" size="sm" className="relative text-gray-700 dark:text-gray-300">
                        <Heart className="h-5 w-5" />
                        {wishlistItems > 0 && (
                          <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-red-500 text-white text-xs">
                            {wishlistItems}
                          </Badge>
                        )}
                      </Button>
                    </Link>

                    <Link href="/cart">
                      <Button variant="ghost" size="sm" className="relative text-gray-700 dark:text-gray-300">
                        <ShoppingCart className="h-5 w-5" />
                        {totalItems > 0 && (
                          <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-amber-900 text-white text-xs">
                            {totalItems}
                          </Badge>
                        )}
                      </Button>
                    </Link>

                    <Link href={isAuthenticated ? "/profile" : "/auth/login"}>
                      <Button variant="ghost" size="sm" className="text-gray-700 dark:text-gray-300">
                        <User className="h-5 w-5" />
                      </Button>
                    </Link>
                  </>
                )}
              </>
            )}

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="lg:hidden text-gray-700 dark:text-gray-300">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] p-0">
                <div className="flex flex-col h-full">
                  {/* Header */}
                  <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <Link href="/" className="flex items-center gap-2">
                      <Image src="/logo.png" alt="" width={150}
                        height={50}
                        className="rounded-lg" />
                    </Link>
                  </div>

                  {/* Navigation */}
                  <nav className="flex-1 p-6">
                    <div className="space-y-4">
                      <Link
                        href="/"
                        className="block text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400 py-2 text-lg"
                      >
                        Home
                      </Link>
                      <Link
                        href="/shop"
                        className="block text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400 py-2 text-lg"
                      >
                        Shop
                      </Link>
                      <Link
                        href="/shop?category=women"
                        className="block text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400 py-2 text-lg"
                      >
                        Women
                      </Link>
                      <Link
                        href="/shop?category=men"
                        className="block text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400 py-2 text-lg"
                      >
                        Men
                      </Link>
                      <Link
                        href="/shop?category=kids"
                        className="block text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400 py-2 text-lg"
                      >
                        Kids
                      </Link>
                      <Link
                        href="/about"
                        className="block text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400 py-2 text-lg"
                      >
                        About Us
                      </Link>
                      <Link
                        href="/contact"
                        className="block text-gray-700 dark:text-gray-300 hover:text-amber-900 dark:hover:text-amber-400 py-2 text-lg"
                      >
                        Contact Us
                      </Link>
                    </div>
                  </nav>

                  {/* Footer */}
                  <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Theme</span>
                      <Button variant="ghost" size="sm" onClick={toggleTheme}>
                        {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  )
}
